import * as React from 'react';
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import { cn } from '../../lib/utils';

/**
 * Tooltip — shadcn (base-vega) on Base UI.
 *
 * Mapping:
 *   Tooltip.Provider → Tooltip.Provider
 *   Tooltip.Root     → Tooltip.Root
 *   Tooltip.Trigger  → Tooltip.Trigger
 *   Tooltip.Content  → Portal + Positioner + Popup (wrapped here)
 *
 * Tooltip uses `data-instant` / `data-open` for animation states (no
 * `data-state="delayed-open"` anymore).
 */
const TooltipProvider = BaseTooltip.Provider;
const Tooltip = BaseTooltip.Root;
const TooltipTrigger = BaseTooltip.Trigger;

type PositionerProps = React.ComponentPropsWithoutRef<typeof BaseTooltip.Positioner>;
type PopupProps = React.ComponentPropsWithoutRef<typeof BaseTooltip.Popup>;

export interface TooltipContentProps extends PopupProps {
  side?: PositionerProps['side'];
  align?: PositionerProps['align'];
  sideOffset?: PositionerProps['sideOffset'];
}

const TooltipContent = React.forwardRef<HTMLDivElement, TooltipContentProps>(
  ({ className, side, align, sideOffset = 4, ...props }, ref) => (
    <BaseTooltip.Portal>
      <BaseTooltip.Positioner side={side} align={align} sideOffset={sideOffset}>
        <BaseTooltip.Popup
          ref={ref}
          className={cn(
            'z-50 overflow-hidden rounded-md bg-foreground px-2.5 py-1.5 text-xs text-background shadow-md',
            'data-[open]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[open]:fade-in-0',
            className,
          )}
          {...props}
        />
      </BaseTooltip.Positioner>
    </BaseTooltip.Portal>
  ),
);
TooltipContent.displayName = 'TooltipContent';

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
